import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import BankAccountService from "services/BankAccountService";
import Utils from "utils";
import openNotification from "utils/notification";

export const getAllBankAccount = createAsyncThunk(
  "bank-account/getAllBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.getAllBankAccount(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getBankAccountById = createAsyncThunk(
  "bank-account/getBankAccountById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.getBankAccountById(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getAllBanks = createAsyncThunk(
  "bank-account/getAllBanks",
  async (data, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.getAllBanks();
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const createBankAccount = createAsyncThunk(
  "bank-account/createBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await BankAccountService.createBankAccount(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const updateBankAccount = createAsyncThunk(
  "bank-account/updateBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await BankAccountService.updateBankAccount(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const deleteBankAccount = createAsyncThunk(
  "bank-account/deleteBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const { id, onSuccess } = data;
      const response = await BankAccountService.deleteBankAccount(id);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const setDefaultBankAccount = createAsyncThunk(
  "bank-account/setDefaultBankAccount",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await BankAccountService.setDefaultBankAccount(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

const initialState = {
  loading: false,
  bankAccountList: [],
  totalItems: 0,
  bankAccountDetail: {},
  bankList: [],
};
export const storeBankAccountSlice = createSlice({
  name: "bank-account",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
    setBankAccount: (state, action) => {
      state.bankAccountList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllBankAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.bankAccountList = action.payload;
        state.totalItems = action.payload.totalItems;
      })
      .addCase(getAllBankAccount.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getBankAccountById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBankAccountById.fulfilled, (state, action) => {
        state.loading = false;
        state.bankAccountDetail = action.payload;
      })
      .addCase(getBankAccountById.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getAllBanks.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllBanks.fulfilled, (state, action) => {
        state.loading = false;
        state.bankList = action.payload;
      })
      .addCase(getAllBanks.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(createBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(createBankAccount.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createBankAccount.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(updateBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBankAccount.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateBankAccount.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(deleteBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteBankAccount.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteBankAccount.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(setDefaultBankAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(setDefaultBankAccount.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(setDefaultBankAccount.rejected, (state, action) => {
        state.loading = false;
      })
      ;
  },
});

export const { showLoading, setBankAccount } = storeBankAccountSlice.actions;

export default storeBankAccountSlice.reducer;
